import { useEffect, useRef, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';

const DOC_TYPE_COLORS = {
  'News Article': '#ef4444', 'Blog Post': '#F59E0B', 'Academic Paper': '#3b82f6',
  'Wiki': '#10b981', 'Forum': '#FDE047', 'Government': '#06b6d4',
  'Video': '#FDE68A', 'PhD Informative': '#FDE047', 'PhD Dissertation': '#FCD34D',
  'Research Paper': '#6d28d9', 'Technical Drawing': '#a855f7', 'Blueprint': '#9333ea',
  'Patent': '#c026d3', 'Instructional Manual': '#d946ef', 'PDF Document': '#dc2626',
  'MS Word Document': '#2563eb', 'Informative': '#14b8a6', 'Webpage': '#64748b',
  'Personal Report (Organic)': '#10b981', 'Personal Report (Collected)': '#14b8a6',
  'Unknown': '#6b7280'
};

const getMarkerColor = (result, colorMode, categoryColorMap) => {
  if (colorMode === 'doctype') {
    return DOC_TYPE_COLORS[result.article_type] || '#6b7280';
  }
  const cat = result.categories?.[0];
  return (cat && categoryColorMap[cat]) || '#6b7280';
};

const createIndividualIcon = (result, colorMode, categoryColorMap) => {
  const color = getMarkerColor(result, colorMode, categoryColorMap);
  return L.divIcon({
    className: '',
    html: `<div style="
      width:14px;height:14px;
      background:${color};
      border:2px solid rgba(255,255,255,0.9);
      border-radius:50%;
      box-shadow:0 0 6px ${color}88, 0 1px 4px rgba(0,0,0,0.4);
      cursor:pointer;
      transition:transform 0.15s;
    " onmouseover="this.style.transform='scale(1.3)'" onmouseout="this.style.transform='scale(1)'"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -8]
  });
};

const buildPopupHtml = (result, colorMode, categoryColorMap) => {
  const cat = result.categories?.[0] || 'Uncategorized';
  const dt = result.article_type || 'Unknown';
  const title = (result.title || result.url || 'Untitled').substring(0, 80);
  const snippet = (result.snippet || result.description || '').substring(0, 180);
  const catColor = categoryColorMap[cat] || '#6b7280';
  const dtColor = DOC_TYPE_COLORS[dt] || '#6b7280';
  const url = result.url || '#';
  const domain = result.root_domain || '';

  return `<div style="padding:8px 10px;max-width:340px;">
    <a href="${url}" target="_blank" rel="noopener" 
       style="color:#e5e7eb;font-size:0.8rem;font-weight:700;text-decoration:none;display:block;line-height:1.3;"
       title="${(result.title || '').replace(/"/g, '&quot;')}">${title}</a>
    ${domain ? `<div style="color:#60a5fa;font-size:0.65rem;margin-top:2px;">${domain}</div>` : ''}
    ${snippet ? `<div style="color:#a1a1aa;font-size:0.72rem;margin-top:5px;line-height:1.4;overflow:hidden;text-overflow:ellipsis;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;">${snippet}</div>` : ''}
    <div style="display:flex;gap:5px;margin-top:6px;flex-wrap:wrap;">
      <span style="padding:2px 7px;border-radius:4px;font-size:0.62rem;background:${catColor}22;color:${catColor};border:1px solid ${catColor}44;">${cat.substring(0, 30)}</span>
      <span style="padding:2px 7px;border-radius:4px;font-size:0.62rem;background:${dtColor}22;color:${dtColor};border:1px solid ${dtColor}44;">${dt}</span>
      ${(result.categories || []).slice(1, 3).map(c => {
        const cc = categoryColorMap[c] || '#6b7280';
        return `<span style="padding:2px 7px;border-radius:4px;font-size:0.62rem;background:${cc}22;color:${cc};border:1px solid ${cc}44;">${c.substring(0, 20)}</span>`;
      }).join('')}
    </div>
  </div>`;
};

// Offset overlapping dots at same coordinates
const applyOffset = (results) => {
  const seen = {};
  return results.map(r => {
    if (!r.latitude || !r.longitude) return r;
    const key = `${parseFloat(r.latitude).toFixed(5)}_${parseFloat(r.longitude).toFixed(5)}`;
    seen[key] = (seen[key] || 0) + 1;
    if (seen[key] > 1) {
      const angle = (seen[key] * 137.5) * (Math.PI / 180);
      const dist = 0.0003 * Math.sqrt(seen[key]);
      return {
        ...r,
        latitude: parseFloat(r.latitude) + dist * Math.cos(angle),
        longitude: parseFloat(r.longitude) + dist * Math.sin(angle)
      };
    }
    return r;
  });
};

const IndividualDotLayer = ({
  results,
  colorMode = 'category',
  categoryColorMap = {},
  onMarkerClick,
  onMarkerHover,
  onMarkerLeave
}) => {
  const map = useMap();
  const layerRef = useRef(null);

  const buildLayer = useCallback(() => {
    if (layerRef.current) {
      map.removeLayer(layerRef.current);
      layerRef.current = null;
    }
    if (!results || results.length === 0) return;

    const group = L.featureGroup();
    const offsetResults = applyOffset(results);

    offsetResults.forEach((result) => {
      if (!result.latitude || !result.longitude) return;

      const marker = L.marker([result.latitude, result.longitude], {
        icon: createIndividualIcon(result, colorMode, categoryColorMap)
      });

      marker.bindPopup(() => buildPopupHtml(result, colorMode, categoryColorMap), {
        maxWidth: 360,
        className: 'individual-dot-popup'
      });

      marker.on('click', () => onMarkerClick?.(result));
      if (onMarkerHover) marker.on('mouseover', (e) => { marker.openPopup(); onMarkerHover(result, e); });
      if (onMarkerLeave) marker.on('mouseout', () => onMarkerLeave());

      group.addLayer(marker);
    });

    group.addTo(map);
    layerRef.current = group;
  }, [results, colorMode, categoryColorMap, map, onMarkerClick, onMarkerHover, onMarkerLeave]);

  useEffect(() => {
    buildLayer();
    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [buildLayer]);

  return null;
};

export { IndividualDotLayer, DOC_TYPE_COLORS };
export default IndividualDotLayer;
